import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial, Float } from '@react-three/drei';
import * as THREE from 'three';

// Generate network nodes and edges
function generateNetwork(nodeCount = 90, connectionThreshold = 3.8) {
  const positions = new Float32Array(nodeCount * 3);
  const spread = 14;

  for (let i = 0; i < nodeCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = (Math.random() * 0.6 + 0.3) * spread;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }

  // Build connections
  const lineVerts = [];
  for (let i = 0; i < nodeCount; i++) {
    for (let j = i + 1; j < nodeCount; j++) {
      const dx = positions[i * 3] - positions[j * 3];
      const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
      const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < connectionThreshold) {
        lineVerts.push(
          positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
          positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2],
        );
      }
    }
  }

  return { nodePositions: positions, linePositions: new Float32Array(lineVerts) };
}

// Traveling particles along edges
function TravelingParticles({ linePositions, count = 60 }) {
  const ref = useRef();

  const { positions, velocities, edgeData } = useMemo(() => {
    const edgeCount = linePositions.length / 6;
    if (edgeCount === 0) return { positions: new Float32Array(0), velocities: [], edgeData: [] };

    const pos = new Float32Array(count * 3);
    const vels = [];
    const edges = [];

    for (let i = 0; i < count; i++) {
      const edgeIdx = Math.floor(Math.random() * edgeCount);
      const t = Math.random();
      const sx = linePositions[edgeIdx * 6];
      const sy = linePositions[edgeIdx * 6 + 1];
      const sz = linePositions[edgeIdx * 6 + 2];
      const ex = linePositions[edgeIdx * 6 + 3];
      const ey = linePositions[edgeIdx * 6 + 4];
      const ez = linePositions[edgeIdx * 6 + 5];

      pos[i * 3] = sx + (ex - sx) * t;
      pos[i * 3 + 1] = sy + (ey - sy) * t;
      pos[i * 3 + 2] = sz + (ez - sz) * t;

      vels.push(0.3 + Math.random() * 0.7);
      edges.push({ idx: edgeIdx, t });
    }

    return { positions: pos, velocities: vels, edgeData: edges };
  }, [linePositions, count]);

  useFrame((_, delta) => {
    if (!ref.current || edgeData.length === 0) return;
    const posArray = ref.current.geometry.attributes.position.array;
    const edgeCount = linePositions.length / 6;

    for (let i = 0; i < edgeData.length; i++) {
      edgeData[i].t += delta * velocities[i] * 0.3;
      if (edgeData[i].t > 1) {
        edgeData[i].t = 0;
        edgeData[i].idx = Math.floor(Math.random() * edgeCount);
      }

      const e = edgeData[i].idx;
      const t = edgeData[i].t;
      posArray[i * 3] = linePositions[e * 6] + (linePositions[e * 6 + 3] - linePositions[e * 6]) * t;
      posArray[i * 3 + 1] = linePositions[e * 6 + 1] + (linePositions[e * 6 + 4] - linePositions[e * 6 + 1]) * t;
      posArray[i * 3 + 2] = linePositions[e * 6 + 2] + (linePositions[e * 6 + 5] - linePositions[e * 6 + 2]) * t;
    }

    ref.current.geometry.attributes.position.needsUpdate = true;
  });

  if (positions.length === 0) return null;

  return (
    <Points ref={ref} positions={positions} stride={3}>
      <PointMaterial
        color="#4ade80"
        size={0.06}
        sizeAttenuation
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </Points>
  );
}

// Main network graph
function NetworkGraph({ scrollProgress }) {
  const groupRef = useRef();
  const linesRef = useRef();

  const { nodePositions, linePositions } = useMemo(() => generateNetwork(), []);

  const lineGeom = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
    return geom;
  }, [linePositions]);

  const lineMat = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: new THREE.Color('#22c55e'),
      transparent: true,
      opacity: 0.12,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += delta * 0.02;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.08) * 0.08;
    // Gentle tilt shift on scroll — keeps scene alive as user scrolls
    groupRef.current.rotation.z = (scrollProgress || 0) * 0.15;
  });

  return (
    <group ref={groupRef}>
      {/* Nodes */}
      <Points positions={nodePositions} stride={3}>
        <PointMaterial
          color="#22c55e"
          size={0.07}
          sizeAttenuation
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </Points>

      {/* Connections */}
      <lineSegments ref={linesRef} geometry={lineGeom} material={lineMat} />

      {/* Traveling particles */}
      <TravelingParticles linePositions={linePositions} count={80} />
    </group>
  );
}

// Ambient floating particles (background dust)
function AmbientParticles({ count = 300 }) {
  const ref = useRef();

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 40;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 40;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 40;
    }
    return pos;
  }, [count]);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.005;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3}>
      <PointMaterial
        color="#6b7280"
        size={0.02}
        sizeAttenuation
        transparent
        opacity={0.4}
        depthWrite={false}
      />
    </Points>
  );
}

// Central glowing nucleus
function Nucleus() {
  const ref = useRef();

  useFrame((state) => {
    if (!ref.current) return;
    const scale = 1 + Math.sin(state.clock.elapsedTime * 1.5) * 0.15;
    ref.current.scale.set(scale, scale, scale);
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.5}>
      <mesh ref={ref}>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshBasicMaterial color="#22c55e" transparent opacity={0.15} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshBasicMaterial color="#4ade80" transparent opacity={0.5} />
      </mesh>
    </Float>
  );
}

// Full scene composition
function Scene({ scrollProgress }) {
  return (
    <>
      <color attach="background" args={['#09090b']} />
      <fog attach="fog" args={['#09090b', 12, 28]} />
      <ambientLight intensity={0.3} />
      <NetworkGraph scrollProgress={scrollProgress} />
      <AmbientParticles />
      <Nucleus />
    </>
  );
}

export default function NetworkCanvas({ scrollProgress = 0, className = '' }) {
  return (
    <div className={className}>
      <Canvas
        camera={{ position: [0, 0, 12], fov: 55 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: false }}
      >
        <Scene scrollProgress={scrollProgress} />
      </Canvas>
    </div>
  );
}
